import * as React from 'react';

import {
  enumInventoryViewMode,
  InventoryViewMode,
} from '@rematter/frontend-lib-api-client';
import { cn, Col, DataGridColumn, Radio, RadioGroup } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import * as state from '../../_state/inventoryLevelsList.state';
import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import {
  CurrencyValue,
  InventoryLevelRecord,
} from '../../types/inventoryLevel';

import { InventoryLevelGroupCell } from './InventoryLevelGroupCell';

import { useTranslations } from '~/modules/inventory/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

type UseInventoryLevelsColumnsArgs = {
  onRowClick?: (cell: { row: InventoryLevelRecord }) => void;
  viewMode?: InventoryViewMode;
};

const formatCurrency = (amount: number, currencyCode: string): string =>
  new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);

type CurrencyValueCellProps = { values: CurrencyValue[] };

const dash = <span className="text-neutral/40">—</span>;

const CurrencyValueCell = ({ values }: CurrencyValueCellProps) => {
  const displayValues = values?.filter((cv) => cv.currencyCode !== 'NEUTRAL');

  if (!displayValues || displayValues.length === 0) {
    return dash;
  }

  return (
    <div className="@CurrencyValueCell flex flex-col gap-0.5">
      {displayValues.map((cv) => (
        <span key={cv.currencyCode} className="text-sm tabular-nums">
          {cv.totalValue != null
            ? formatCurrency(cv.totalValue, cv.currencyCode)
            : dash}
        </span>
      ))}
    </div>
  );
};

type WACCellProps = { values: CurrencyValue[] };

const WACCell = ({ values }: WACCellProps) => {
  const displayValues = values?.filter((cv) => cv.currencyCode !== 'NEUTRAL');

  if (!displayValues || displayValues.length === 0) {
    return dash;
  }

  return (
    <div className="@WACCell flex flex-col gap-0.5">
      {displayValues.map((cv) => (
        <span key={cv.currencyCode} className="text-sm tabular-nums">
          {cv.weightedAverageCost != null
            ? formatCurrency(cv.weightedAverageCost, cv.currencyCode)
            : dash}
        </span>
      ))}
    </div>
  );
};

export const useInventoryLevelsColumns = ({
  onRowClick,
  viewMode = enumInventoryViewMode.OurMaterial,
}: UseInventoryLevelsColumnsArgs = {}): DataGridColumn<InventoryLevelRecord>[] => {
  const t = useTranslations();
  const { formatQuantityValue } = useInventoryUnit();
  const [quantityFilter, setQuantityFilter] = useAtom(state.$quantityFilter);

  return React.useMemo(() => {
    const columns: DataGridColumn<InventoryLevelRecord>[] = [
      {
        key: 'item',
        header: t.inventoryLevelsView.table.columns.item,
        onClick: onRowClick,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">
              {row.itemName || `Item ${row.itemId}`}
            </span>
            {row.itemSku && (
              <span className="text-xs text-neutral/60">{row.itemSku}</span>
            )}
          </div>
        ),
        sortable: true,
        groupCell: (group) => <InventoryLevelGroupCell {...group} />,
      },
    ];

    // For Tolling Material view, show Owner column first (after Item)
    if (viewMode === enumInventoryViewMode.TollingMaterial) {
      columns.push({
        key: 'owner',
        header: t.inventoryLevelsView.table.columns.owner,
        onClick: onRowClick,
        cell: ({ row }) =>
          row.ownerAccountName ? (
            <span className="text-sm">{row.ownerAccountName}</span>
          ) : (
            <span className="text-neutral/40">—</span>
          ),
        width: 180,
      });
    }

    columns.push(
      {
        key: 'quantity',
        header: t.inventoryLevelsView.table.columns.quantity,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span
            className={cn(
              'font-semibold tabular-nums',
              row.quantity < 0 && 'text-failure',
              row.quantity > 0 && 'text-success',
              row.quantity === 0 && 'text-neutral/60',
            )}
          >
            {formatQuantityValue(row.quantity, row.quantityUnit)}
          </span>
        ),
        width: 120,
        sortable: true,
        isFilterActive: quantityFilter !== 'all',
        columnFilter: () => (
          <Col className="p-3">
            <RadioGroup
              value={quantityFilter}
              onChange={(v) => setQuantityFilter(v as state.QuantityFilter)}
            >
              <Radio value="all">
                {t.inventoryLevelsView.filters.allQuantities}
              </Radio>
              <Radio value="hideZero">
                {t.inventoryLevelsView.filters.hideZeroQuantity}
              </Radio>
              <Radio value="negativeOnly">
                {t.inventoryLevelsView.filters.negativeOnly}
              </Radio>
            </RadioGroup>
          </Col>
        ),
      },
      {
        key: 'totalValue',
        header: t.inventoryLevelsView.table.columns.totalValue,
        onClick: onRowClick,
        cell: ({ row }) => <CurrencyValueCell values={row.currencyValues} />,
        width: 130,
      },
      {
        key: 'wac',
        header: t.inventoryLevelsView.table.columns.wac,
        onClick: onRowClick,
        cell: ({ row }) => <WACCell values={row.currencyValues} />,
        width: 130,
      },
    );

    return columns;
  }, [
    t,
    onRowClick,
    formatQuantityValue,
    viewMode,
    quantityFilter,
    setQuantityFilter,
  ]);
};
